import React, { useEffect, useRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Box, Typography } from '@mui/material';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import { SiteGroup, SiteMap, hasValidCoords, markerType } from '../utils/siteUtils';
import { ServerRecord } from '../data/inventoryData';

const PIN_COLORS: Record<string, string> = {
  gpu: '#10b981',
  physical: '#DB1E36',
  vm: '#0047BA',
};

function createPinIcon(type: 'gpu' | 'physical' | 'vm') {
  const color = PIN_COLORS[type];
  return L.divIcon({
    className: '',
    html: `<div style="width:22px;height:22px;background:${color};border:3px solid #fff;border-radius:50% 50% 50% 0;transform:rotate(-45deg);box-shadow:0 3px 9px rgba(16,32,60,.35)"></div>`,
    iconSize: [22, 22],
    iconAnchor: [11, 22],
    popupAnchor: [0, -24],
  });
}

const LEGEND_ITEMS = [
  { type: 'gpu', label: 'GPU-enabled site', color: PIN_COLORS.gpu },
  { type: 'physical', label: 'Physical site', color: PIN_COLORS.physical },
  { type: 'vm', label: 'Virtual machine site', color: PIN_COLORS.vm },
];

interface Props {
  sites: SiteMap;
  visibleSiteNames: Set<string>;
  matchFn: (s: ServerRecord) => boolean;
  onSiteClick: (site: SiteGroup) => void;
}

export default function InfraLeafletMap({
  sites,
  visibleSiteNames,
  matchFn,
  onSiteClick,
}: Props) {
  const mapRef = useRef<HTMLDivElement | null>(null);
  const leafletMap = useRef<L.Map | null>(null);
  const markersRef = useRef<Record<string, L.Marker>>({});

  // Initialise the map once on mount
  useEffect(() => {
    if (mapRef.current && !leafletMap.current) {
      const map = L.map(mapRef.current).setView([39.9626, -76.7277], 5);
      L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '© OpenStreetMap',
      }).addTo(map);
      leafletMap.current = map;
    }
    return () => {
      leafletMap.current?.remove();
      leafletMap.current = null;
    };
  }, []);

  // Re-render markers whenever sites or filters change
  useEffect(() => {
    const map = leafletMap.current;
    if (!map) return;

    Object.values(markersRef.current).forEach((m) => m.remove());
    markersRef.current = {};

    const bounds: L.LatLngTuple[] = [];

    Object.entries(sites).forEach(([name, site]) => {
      // Skip sites whose records have no usable Latitude/Longitude —
      // Leaflet throws on invalid coordinates, which would blank the map.
      if (!hasValidCoords(site.Latitude, site.Longitude)) return;

      const type = markerType(site);
      const marker = L.marker([site.Latitude, site.Longitude], {
        icon: createPinIcon(type),
        opacity: visibleSiteNames.has(name) ? 1 : 0.15,
      });

      marker.on('click', () => {
        onSiteClick(site);
      });

      marker.addTo(map);
      markersRef.current[name] = marker;
      bounds.push([site.Latitude, site.Longitude]);
    });

    if (bounds.length) map.fitBounds(bounds);
  }, [sites, visibleSiteNames, matchFn, onSiteClick]);

  return (
    <Box sx={{ position: 'relative', flex: 1, minHeight: 0, height: '100%' }}>
      <div ref={mapRef} style={{ height: '100%', width: '100%', borderRadius: 8 }} />

      <Box
        sx={{
          position: 'absolute',
          bottom: 24,
          right: 12,
          zIndex: 1000,
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: 'grayscale.400',
          borderRadius: 2,
          p: 1.5,
          boxShadow: 3,
          pointerEvents: 'none',
        }}
      >
        <Typography variant="caption" fontWeight={700} display="block" mb={0.5}>
          Legend
        </Typography>
        {LEGEND_ITEMS.map(({ type, label, color }) => (
          <Box key={type} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <FiberManualRecordIcon sx={{ fontSize: 12, color }} />
            <Typography variant="caption">{label}</Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}
